import React, { useState, useRef } from 'react';
import { collection, addDoc, query, where, getDocs, limit, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import employeeService from '../../firebase/employeeService';
import './CSVAttendanceImport.css';

/**
 * CSVAttendanceImport Component
 * Allows bulk import of attendance records via CSV file upload
 */
const CSVAttendanceImport = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [step, setStep] = useState('upload'); // upload, validate, import, complete
  const fileInputRef = useRef(null);

  // Warehouse coordinates (Citilink Warehouse Complex)
  const warehouseCoords = {
    latitude: 1.2762970485308482,
    longitude: 103.79506703099759
  };

  // Default coordinates for manual entries
  const defaultCoords = {
    latitude: 1.3521,
    longitude: 103.8198
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      parseCSV(selectedFile);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  // Parse CSV file
  const parseCSV = (file) => {
    setProcessing(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          throw new Error('CSV file must contain at least a header row and one data row');
        }

        // Parse header
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const requiredHeaders = ['employeeid', 'date', 'clockin', 'clockout'];
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        
        if (missingHeaders.length > 0) {
          throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
        }

        // Parse data rows
        const data = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          if (values.length >= headers.length && values.some(v => v)) {
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            row.lineNumber = i + 1;
            data.push(row);
          }
        }

        setCsvData(data);
        setStep('validate');
        validateData(data);
      } catch (error) {
        alert(`Error parsing CSV: ${error.message}`);
        setFile(null);
      } finally {
        setProcessing(false);
      }
    };

    reader.readAsText(file);
  };

  // Validate CSV data
  const validateData = async (data) => {
    setProcessing(true);
    
    try {
      // Load employees for validation
      const employeeResult = await employeeService.getEmployees();
      const employeeList = employeeResult.success ? employeeResult.employees : [];
      setEmployees(employeeList);

      const validationResults = [];
      
      for (const row of data) {
        const result = {
          lineNumber: row.lineNumber,
          data: row,
          errors: [],
          warnings: [],
          valid: true
        };

        // Validate employee ID
        const employee = employeeList.find(emp => emp.employeeId === row.employeeid);
        if (!employee) {
          result.errors.push(`Employee ID '${row.employeeid}' not found`);
          result.valid = false;
        } else {
          result.employee = employee;
        }

        // Validate date format
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(row.date)) {
          result.errors.push('Date must be in YYYY-MM-DD format');
          result.valid = false;
        } else {
          const date = new Date(row.date);
          if (isNaN(date.getTime())) {
            result.errors.push('Invalid date');
            result.valid = false;
          } else if (date > new Date()) {
            result.warnings.push('Future date detected');
          }
        }

        // Validate time formats
        const timeRegex = /^\d{1,2}:\d{2}$/;
        if (row.clockin && !timeRegex.test(row.clockin)) {
          result.errors.push('Clock-in time must be in HH:MM format');
          result.valid = false;
        }
        if (row.clockout && !timeRegex.test(row.clockout)) {
          result.errors.push('Clock-out time must be in HH:MM format');
          result.valid = false;
        }

        // Check if at least one time is provided
        if (!row.clockin && !row.clockout) {
          result.errors.push('At least one of clock-in or clock-out time must be provided');
          result.valid = false;
        }

        // Validate time logic
        if (row.clockin && row.clockout) {
          const clockInTime = new Date(`${row.date}T${row.clockin}`);
          const clockOutTime = new Date(`${row.date}T${row.clockout}`);
          if (clockOutTime <= clockInTime) {
            result.warnings.push('Clock-out time should be after clock-in time');
          }
        }

        validationResults.push(result);
      }

      setValidationResults(validationResults);
    } catch (error) {
      alert(`Error validating data: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Import validated data
  const handleImport = async () => {
    const validRecords = validationResults.filter(r => r.valid);
    if (validRecords.length === 0) {
      alert('No valid records to import');
      return;
    }

    setImporting(true);
    setStep('import');

    const importResults = {
      total: validRecords.length,
      successful: 0,
      failed: 0,
      errors: []
    };

    try {
      for (const record of validRecords) {
        try {
          const { data: row, employee } = record;
          const isWarehouse = row.location === 'warehouse';
          const coords = isWarehouse ? warehouseCoords : defaultCoords;

          // Create clock-in record
          if (row.clockin) {
            const clockInDateTime = new Date(`${row.date}T${row.clockin}`);
            const clockInRecord = {
              uid: employee.uid,
              employeeId: employee.employeeId,
              type: 'clock-in',
              timestamp: clockInDateTime,
              location: {
                latitude: coords.latitude,
                longitude: coords.longitude,
                accuracy: 10
              },
              isWarehouse: isWarehouse,
              photoUrl: 'manual-entry-no-photo',
              photoMetadata: {
                captureTime: clockInDateTime,
                deviceInfo: { source: 'csv-import' },
                isLiveCapture: false
              },
              status: 'approved',
              validationResults: {
                photoRecency: true,
                gpsAccuracy: true,
                warehouseProximity: isWarehouse,
                duplicateCheck: true,
                photoAuthenticity: false,
                overallScore: 85
              },
              isManualEntry: true,
              manualEntryReason: row.reason || 'CSV import',
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            };

            await addDoc(collection(db, 'attendance'), clockInRecord);
            importResults.successful++;
          }

          // Create clock-out record
          if (row.clockout) {
            const clockOutDateTime = new Date(`${row.date}T${row.clockout}`);
            const clockOutRecord = {
              uid: employee.uid,
              employeeId: employee.employeeId,
              type: 'clock-out',
              timestamp: clockOutDateTime,
              location: {
                latitude: coords.latitude,
                longitude: coords.longitude,
                accuracy: 10
              },
              isWarehouse: isWarehouse,
              photoUrl: 'manual-entry-no-photo',
              photoMetadata: {
                captureTime: clockOutDateTime,
                deviceInfo: { source: 'csv-import' },
                isLiveCapture: false
              },
              status: 'approved',
              validationResults: {
                photoRecency: true,
                gpsAccuracy: true,
                warehouseProximity: isWarehouse,
                duplicateCheck: true,
                photoAuthenticity: false,
                overallScore: 85
              },
              isManualEntry: true,
              manualEntryReason: row.reason || 'CSV import',
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            };

            await addDoc(collection(db, 'attendance'), clockOutRecord);
            importResults.successful++;
          }

          // Update employee's last attendance timestamp
          await updateDoc(doc(db, 'employees', employee.uid), {
            lastAttendanceAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });

        } catch (error) {
          importResults.failed++;
          importResults.errors.push({
            line: record.lineNumber,
            employee: record.data.employeeid,
            error: error.message
          });
        }

        // Small delay to avoid overwhelming Firebase
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      setResults(importResults);
      setStep('complete');

      if (onSuccess) {
        onSuccess(importResults);
      }

    } catch (error) {
      alert(`Import failed: ${error.message}`);
    } finally {
      setImporting(false);
    }
  };

  // Download sample CSV
  const downloadSample = () => {
    const sampleData = [
      'employeeId,date,clockIn,clockOut,location,reason',
      'BS13H005,2025-09-10,12:00,18:00,manual-entry,Admin added past attendance record',
      'BS13H005,2025-09-11,14:00,22:30,manual-entry,Admin added past attendance record'
    ].join('\n');

    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'attendance-import-sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const validCount = validationResults.filter(r => r.valid).length;
  const invalidCount = validationResults.filter(r => !r.valid).length;

  return (
    <div className="csv-import-modal">
      <div className="csv-import-container">
        <div className="import-header">
          <h3>📊 CSV Attendance Import</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="import-step">
            <div className="step-content">
              <h4>Step 1: Upload CSV File</h4>
              <p>Upload a CSV file with attendance data. The file must include columns: employeeId, date, clockIn, clockOut</p>
              
              <div className="file-upload-area">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                />
                <button 
                  className="upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={processing}
                >
                  {processing ? '⏳ Processing...' : '📁 Select CSV File'}
                </button>
                {file && <p className="file-name">Selected: {file.name}</p>}
              </div>

              <div className="sample-download">
                <button className="sample-btn" onClick={downloadSample}>
                  📥 Download Sample CSV
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Validate */}
        {step === 'validate' && (
          <div className="import-step">
            <div className="step-content">
              <h4>Step 2: Validation Results</h4>
              <div className="validation-summary">
                <div className="summary-item valid">
                  ✅ Valid Records: {validCount}
                </div>
                <div className="summary-item invalid">
                  ❌ Invalid Records: {invalidCount}
                </div>
              </div>

              <div className="validation-details">
                {validationResults.map((result, index) => (
                  <div key={index} className={`validation-row ${result.valid ? 'valid' : 'invalid'}`}>
                    <div className="row-header">
                      <span className="line-number">Line {result.lineNumber}</span>
                      <span className="employee-id">{result.data.employeeid}</span>
                      <span className="date">{result.data.date}</span>
                    </div>
                    {result.errors.length > 0 && (
                      <div className="errors">
                        {result.errors.map((error, i) => (
                          <div key={i} className="error">❌ {error}</div>
                        ))}
                      </div>
                    )}
                    {result.warnings.length > 0 && (
                      <div className="warnings">
                        {result.warnings.map((warning, i) => (
                          <div key={i} className="warning">⚠️ {warning}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="step-actions">
                <button className="btn-secondary" onClick={() => setStep('upload')}>
                  ← Back
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleImport}
                  disabled={validCount === 0}
                >
                  Import {validCount} Records →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Import Progress */}
        {step === 'import' && (
          <div className="import-step">
            <div className="step-content">
              <h4>Step 3: Importing Records</h4>
              <div className="import-progress">
                <div className="progress-spinner"></div>
                <p>Importing attendance records to Firebase...</p>
                <p>Please wait, this may take a few moments.</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 'complete' && results && (
          <div className="import-step">
            <div className="step-content">
              <h4>Step 4: Import Complete</h4>
              <div className="import-results">
                <div className="result-summary">
                  <div className="result-item success">
                    ✅ Successfully imported: {results.successful} records
                  </div>
                  {results.failed > 0 && (
                    <div className="result-item error">
                      ❌ Failed to import: {results.failed} records
                    </div>
                  )}
                </div>

                {results.errors.length > 0 && (
                  <div className="error-details">
                    <h5>Import Errors:</h5>
                    {results.errors.map((error, index) => (
                      <div key={index} className="error-item">
                        Line {error.line} ({error.employee}): {error.error}
                      </div>
                    ))}
                  </div>
                )}

                <div className="next-steps">
                  <h5>Next Steps:</h5>
                  <ul>
                    <li>Check your Attendance Monitoring dashboard</li>
                    <li>Filter by the imported date range to see new records</li>
                    <li>All imported records are marked as manual entries</li>
                    <li>Records are automatically approved and ready for use</li>
                  </ul>
                </div>
              </div>

              <div className="step-actions">
                <button className="btn-primary" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="import-help">
          <h5>💡 CSV Format Requirements:</h5>
          <ul>
            <li><strong>employeeId:</strong> Must match existing employee ID in system</li>
            <li><strong>date:</strong> Format YYYY-MM-DD (e.g., 2025-09-10)</li>
            <li><strong>clockIn:</strong> Format HH:MM (e.g., 08:30) - optional</li>
            <li><strong>clockOut:</strong> Format HH:MM (e.g., 17:30) - optional</li>
            <li><strong>location:</strong> 'warehouse' or 'manual-entry' (optional)</li>
            <li><strong>reason:</strong> Reason for manual entry (optional)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CSVAttendanceImport;
